import type { TFunction } from 'i18next';
import type { CostsToDate, Initiative } from '../../../../api/initiative-client/initiative-client.type';
import type { ProjectsResponse } from '../../../../api/projects-client/projects-client.type';
import type { OutcomeCostsToDate } from '../../../../api/strategy-client/strategy-client.type';
import type { Epic } from '../../../../api/tasks-client/task-client.type';
import { graphChefchaouenBlue, secondaryBase } from '../../../../styles/design-tokens';
import { Icon } from '../../../../ui-library/icon/icon';
import type { ExtendedComboboxItem } from '../../../../ui-library/select/select.type';
import { StrategyTab } from '../../strategy.type';
import { ChartDataType, CostMode, type ChartDataItem } from './cost-tab.type';

/**
 * Percentage threshold for epic/team visibility in the tree map.
 * Epics/teams with a percentage below this threshold will be grouped into the "Other" category.
 * This improves visualization clarity by preventing the tree map from becoming cluttered with tiny segments
 * that would be difficult to interact with and visually interpret.
 */
const THRESHOLD = 5;

/**
 * Minimum size (in percentage) for individual initiative cells in the tree map.
 * Initiatives with sizes between THRESHOLD and MIN_CELL_SIZE will be artificially
 * increased to this size to ensure they remain clickable and visible.
 */
const MIN_CELL_SIZE = 8;

/**
 * Calculates the percentage of a value relative to a total
 *
 * @param {number} value - The value to calculate percentage for
 * @param {number} total - The total value
 * @returns {number} The percentage rounded to 2 decimal places
 */
const getPercentage = (value: number, total: number): number => {
  if (total === 0) return 0;
  return Math.round((value / total) * 100 * 100) / 100;
};

/**
 * Generate tree map data for entities (epics, teams, or initiatives) based on cost data
 *
 * @param {Record<string, number>} costData - The cost data object mapping entity IDs to costs
 * @param {T[]} entities - Array of entities to match with cost data
 * @param {ChartDataType} entityType - The type of entity (Epic, Team, or Initiative)
 * @param {(entity: T) => string} getId - Function to get the ID from an entity
 * @param {(entity: T) => string} getTitle - Function to get the title from an entity
 * @param {(entity: T) => string | undefined} getName - Optional function to get the name from an entity
 * @returns {ChartDataItem[] | []} Array of chart data items for the tree map visualization or empty array if no data
 */
const generateTreeMapData = <T,>(
  costData: Record<string, number> | undefined,
  entities: T[],
  entityType: ChartDataType,
  getId: (entity: T) => string,
  getTitle: (entity: T) => string,
  t: TFunction,
  getName?: (entity: T) => string,
): ChartDataItem[] | [] => {
  if (!costData || Object.keys(costData).length === 0) {
    return [];
  }

  const totalCost = Object.values(costData).reduce((acc, cost) => acc + cost, 0);

  const rawData = Object.entries(costData)
    .map(([entityId, cost]) => {
      const entity = entities.find((e) => getId(e) === entityId);

      if (!entity) return null;

      const percentage = getPercentage(cost, totalCost);
      let displayPercentage = percentage;
      if (percentage >= THRESHOLD && percentage < MIN_CELL_SIZE) {
        displayPercentage = MIN_CELL_SIZE;
      }

      const baseData = {
        id: entityId,
        title: getTitle(entity),
        size: displayPercentage,
        actualSize: cost,
        percentage,
        fill: graphChefchaouenBlue,
        type: entityType,
      };

      // Add name field only if getName function is provided
      return getName ? { ...baseData, name: getName(entity) } : baseData;
    })
    .filter(Boolean) as ChartDataItem[];

  const dataType =
    entityType === ChartDataType.Epic ? 'epics' : entityType === ChartDataType.Team ? 'teams' : 'initiatives';
  return calculateTreeMapData(rawData, dataType, t);
};

/**
 * Generate tree map data for epics based on costs_to_date
 *
 * @param {CostsToDate} costs - The costs to date information containing epic cost data
 * @param {Epic[]} epics - Array of epics to match with cost data
 * @returns {ChartDataItem[] | []} Array of chart data items for the tree map visualization or empty array if no data
 */
const generateEpicsTreeMapData = (costs: CostsToDate, epics: Epic[], t: TFunction): ChartDataItem[] | [] => {
  return generateTreeMapData(
    costs.epics,
    epics,
    ChartDataType.Epic,
    (epic) => epic.id,
    (epic) => epic.title,
    t,
    (epic) => epic.name,
  );
};

/**
 * Generate tree map data for teams based on costs_to_date
 *
 * @param {CostsToDate} costs - The costs to date information containing team cost data
 * @param {ProjectsResponse[]} teams - Array of teams to match with cost data
 * @returns {ChartDataItem[] | []} Array of chart data items for the tree map visualization or empty array if no data
 */
const generateTeamsTreeMapData = (
  costs: CostsToDate,
  teams: ProjectsResponse[],
  t: TFunction,
): ChartDataItem[] | [] => {
  return generateTreeMapData(
    costs.teams,
    teams,
    ChartDataType.Team,
    (team) => team.id,
    (team) => team.name,
    t,
  );
};

/**
 * Generate tree map data for initiatives based on OutcomeCostsToDate
 *
 * @param {OutcomeCostsToDate} costs - The costs to date information containing initiative cost data
 * @param {Initiative[]} initiatives - Array of initiatives to match with cost data
 * @returns {ChartDataItem[] | []} Array of chart data items for the tree map visualization or empty array if no data
 */
const generateInitiativesTreeMapData = (
  costs: OutcomeCostsToDate,
  initiatives: Initiative[],
  t: TFunction,
): ChartDataItem[] | [] => {
  return generateTreeMapData(
    costs.initiatives,
    initiatives,
    ChartDataType.Initiative,
    (initiative) => initiative.id,
    (initiative) => initiative.key ?? initiative.name ?? '',
    t,
    (initiative) => initiative.name,
  );
};

/**
 * Calculates tree map data for visualization based on chart data items.
 *
 * This function processes chart data to create a tree map visualization structure.
 * It separates items into two categories:
 * 1. Visible items - those with percentage values above the defined threshold
 * 2. "Other" items - those with percentage values below the threshold
 *
 * The "Other" tile is guaranteed to have a minimum size for visibility and clickability,
 * even if the combined small items are very tiny.
 *
 * @param {ChartDataItem[]} rawData - Array of raw chart data items
 * @param {'epics' | 'teams' | 'initiatives'} dataType - Type of data being processed
 * @returns {ChartDataItem[]} Processed data array ready for tree map visualization
 */
const calculateTreeMapData = (
  rawData: ChartDataItem[],
  dataType: 'epics' | 'teams' | 'initiatives',
  t: TFunction,
): ChartDataItem[] => {
  const visibleItems = rawData.filter((item) => item.percentage >= THRESHOLD);
  const otherItems = rawData.filter((item) => item.percentage < THRESHOLD);

  if (otherItems.length === 0) {
    return visibleItems.sort((a, b) => b.size - a.size);
  }

  const otherCost = otherItems.reduce((acc, item) => acc + item.actualSize, 0);
  const otherPercentage = otherItems.reduce((acc, item) => acc + item.percentage, 0);

  const otherData: Record<string, { title: string; size: number; percentage: number }> = {};
  otherItems.forEach((item) => {
    otherData[item.id] = { title: item.title, size: item.actualSize, percentage: item.percentage };
  });

  // Ensure minimum size for "Other" tile
  const displaySize = Math.max(otherPercentage, calculateMinimumSizeValue(rawData, otherPercentage));

  const otherDataKey =
    dataType === 'epics' ? 'otherEpicsData' : dataType === 'teams' ? 'otherTeamsData' : 'otherInitiativesData';

  const otherItem: ChartDataItem = {
    id: 'other',
    title: t('strategy.initiatives.other'),
    size: displaySize,
    actualSize: otherCost,
    percentage: otherPercentage,
    fill: graphChefchaouenBlue,
    type: ChartDataType.Other,
    [otherDataKey]: otherData,
  };

  return [...visibleItems.sort((a, b) => b.size - a.size), otherItem];
};

/**
 * Calculates the minimum size value for the "Other" tile based on the total data
 *
 * @param {ChartDataItem[]} rawData - Array of raw chart data items
 * @param {number} actualOtherSize - The actual combined size of other items
 * @returns {number} The minimum size value to ensure proper visibility
 */
const calculateMinimumSizeValue = (rawData: ChartDataItem[], actualOtherSize: number): number => {
  const totalSize = rawData.reduce((acc, item) => acc + item.size, 0);

  // If total size is zero, use fallback minimum to ensure visibility
  if (totalSize === 0) {
    return MIN_CELL_SIZE;
  }

  const minimumSize = (totalSize * MIN_CELL_SIZE) / 100;

  return Math.max(minimumSize, actualOtherSize);
};

/**
 * Returns a list of options for the cost mode dropdown
 * Each option contains a label for display and a value from the CostMode enum
 *
 * @param {StrategyTab} currentStrategyTab - The currently active strategy tab
 * @returns {ExtendedComboboxItem[]} Array of options for the cost mode selection
 */
const getCostModeOptions = (currentStrategyTab: StrategyTab, t: TFunction): ExtendedComboboxItem[] => {
  const options = [
    {
      label: t('strategy.initiative_performance.labels.contributing_epics'),
      value: CostMode.ContributingEpics,
      leftSection: <Icon name="move_down" size={20} color={secondaryBase} />,
    },
    {
      label: t('strategy.initiative_performance.labels.contributing_teams'),
      value: CostMode.ContributingTeams,
      leftSection: <Icon name="people_alt" size={20} color={secondaryBase} />,
    },
    {
      label: t('strategy.initiative_performance.labels.contributing_initiatives'),
      value: CostMode.ContributingInitiatives,
      leftSection: <Icon name="move_down" size={20} color={secondaryBase} />,
    },
  ];
  switch (currentStrategyTab) {
    case StrategyTab.Goals:
      return [options[1], options[2]];
    case StrategyTab.Outcomes:
      return [options[1], options[2]];
    default:
      return [options[0], options[1]];
  }
};

export { generateEpicsTreeMapData, generateInitiativesTreeMapData, generateTeamsTreeMapData, getCostModeOptions };
